import { HttpClient } from '@angular/common/http';
import { inject, Injectable, InjectionToken } from '@angular/core';
import { concatMap, from, ignoreElements, scan } from 'rxjs';

export type LaravelResult<
  T extends
    | unknown
    | string
    | string[]
    | Record<string, unknown>
    | Record<string, unknown>[],
  V extends { responderVersion: string } = { responderVersion: string },
> = {
  data: T;
  meta: V;
};

export type LaravelPagination = {
  total: number;
  perPage: number;
  currentPage: number;
  lastPage: number;
};

export const LARAVEL_ENDPOINT = new InjectionToken<string>(
  'laravel.endpoint.url',
);

@Injectable()
export class LaravelApi<T extends Record<string, unknown>> {
  #http = inject(HttpClient);
  #url = inject(LARAVEL_ENDPOINT);

  readOne(id: number | string) {
    return this.#http.get<LaravelResult<T>>(`${this.#url}/${id}`);
  }
  readMany(query = '') {
    return this.#http.get<
      LaravelResult<
        T[],
        {
          responderVersion: string;
          pagination: LaravelPagination;
        }
      >
    >(`${this.#url}${query}`);
  }
  addOne(createData: Partial<T>) {
    return this.#http.post<LaravelResult<T>>(this.#url, createData);
  }
  addMany(createDataCollection: Partial<T>[]) {
    return from(createDataCollection).pipe(
      concatMap((createData) => this.addOne(createData)),
      scan(
        (acc, cur) => ({
          data: [...acc.data, cur.data],
          meta: acc.meta ?? cur.meta,
        }),
        { data: [] as T[], meta: undefined } as unknown as LaravelResult<T[]>,
      ),
    );
  }
  updateOne(id: number | string, updateData: Partial<T>) {
    return this.#http.put<LaravelResult<T>>(`${this.#url}/${id}`, updateData);
  }
  updateMany(updateDataCollection: { [id: string]: Partial<T> }) {
    return from(Object.entries(updateDataCollection)).pipe(
      concatMap(([id, updateData]) => this.updateOne(id, updateData)),
      scan(
        (acc, cur) => ({
          data: [...acc.data, cur.data],
          meta: acc.meta ?? cur.meta,
        }),
        { data: [] as T[], meta: undefined } as unknown as LaravelResult<T[]>,
      ),
    );
  }
  removeOne(id: number | string) {
    return this.#http.delete<void>(`${this.#url}/${id}`);
  }
  removeMany(ids: number[] | string[]) {
    return from(ids).pipe(
      concatMap((id) => this.removeOne(id)),
      ignoreElements(),
    );
  }
}
